// Browser-side fallback for videos the current browser cannot decode natively.
//
// Edge ships without a software AV1 decoder and needs a Store extension for
// HEVC, so a file that plays in Chrome can decode audio only there, leaving a
// silent black frame. Since the API cannot be changed, the remaining option is
// to decode in the page: transcode to H.264 with ffmpeg.wasm and hand the
// result back as a blob URL.
//
// No npm install: the UMD build is injected as a <script> at runtime and read
// off window, and the two @ffmpeg/util helpers we need are reimplemented below.
// To drop the external dependency entirely, copy ffmpeg.js, 814.ffmpeg.js,
// ffmpeg-core.js and ffmpeg-core.wasm into public/ and point FFMPEG_BASE /
// CORE_BASE at your own origin.
//
// The single-threaded core is used on purpose - the multithreaded build needs
// SharedArrayBuffer, which requires COOP/COEP response headers we cannot set.

const FFMPEG_VERSION = '0.12.10';
const CORE_VERSION = '0.12.6';
const FFMPEG_BASE = `https://unpkg.com/@ffmpeg/ffmpeg@${FFMPEG_VERSION}/dist/umd`;
const FFMPEG_UMD_URL = `${FFMPEG_BASE}/ffmpeg.js`;
// Webpack chunk holding the FFmpeg worker in the 0.12.10 UMD build. The name is
// build-specific: check dist/umd/ when bumping FFMPEG_VERSION.
const FFMPEG_WORKER_URL = `${FFMPEG_BASE}/814.ffmpeg.js`;
// ESM core, not UMD: passing classWorkerURL makes FFmpeg spawn a module worker,
// where importScripts throws and the core is loaded with import() instead -
// which needs the build that has `export default createFFmpegCore`.
const CORE_BASE = `https://unpkg.com/@ffmpeg/core@${CORE_VERSION}/dist/esm`;

// interactive: someone is watching and waiting; background: uploads
export type TranscodeLane = 'interactive' | 'background';

export interface TranscodeOptions {
  // Long side cap in pixels; smaller sources are left at their size
  maxLongSide: number;
  // x264 speed/size trade-off: 'ultrafast' | 'superfast' | 'veryfast' | ...
  preset: string;
  // x264 quality, lower is better (18-28 is the useful range)
  crf: number;
  // Which converter pool runs the job (see `pools`)
  lane: TranscodeLane;
}

// For playback fallback: the result is a throwaway blob, so speed wins.
// 720p decodes fine anywhere and keeps the single-threaded encode several
// times faster than a full-size 1080p/4K one.
export const PLAYBACK_TRANSCODE: TranscodeOptions = {
  maxLongSide: 1280,
  preset: 'ultrafast',
  crf: 28,
  lane: 'interactive',
};

// Shrinks only (min with the source size); -2 keeps the aspect and an even size
function scaleFilter(maxLongSide: number): string
{
  return `scale='if(gte(iw,ih),min(${maxLongSide},iw),-2)':'if(gte(iw,ih),-2,min(${maxLongSide},ih))'`;
}

// Converted clips kept for the session, keyed by source URL. Small on purpose:
// each entry holds a whole video in memory.
const CACHE_LIMIT = 3;
const transcodeCache = new Map<string, Blob>();

function rememberTranscode(url: string, blob: Blob): void
{
  transcodeCache.delete(url);
  transcodeCache.set(url, blob);
  while (transcodeCache.size > CACHE_LIMIT)
  {
    const oldest = transcodeCache.keys().next().value;
    if (oldest === undefined) break;
    transcodeCache.delete(oldest);
  }
}

export interface TranscodeProgress {
  // 0..1, or null before encoding starts
  ratio: number | null;
  // queued: waiting for a free converter while others run
  stage: 'queued' | 'loading-core' | 'transcoding';
}

let scriptPromise: Promise<void> | null = null;

function loadScript(src: string): Promise<void>
{
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise<void>((resolve, reject) =>
  {
    if (document.querySelector<HTMLScriptElement>(`script[src="${src}"]`))
    {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () =>
    {
      scriptPromise = null;
      reject(new Error(`Failed to load ${src}`));
    };
    document.head.appendChild(script);
  });

  return scriptPromise;
}

// Stands in for @ffmpeg/util's toBlobURL: the ffmpeg worker cannot load the
// core cross-origin, so fetch it here and hand over a same-origin blob URL.
async function toBlobURL(url: string, mimeType: string): Promise<string>
{
  const response = await fetch(url);
  if (!response.ok)
  {
    throw new Error(`Failed to fetch ${url}: ${response.status}`);
  }

  const buffer = await response.arrayBuffer();
  return URL.createObjectURL(new Blob([buffer], { type: mimeType }));
}

// Stands in for @ffmpeg/util's fetchFile. Needs CORS on the file host.
// no-store: the <video> element already cached this URL without CORS headers,
// and revalidating that entry yields a 304 the CORS check rejects.
async function fetchFile(url: string): Promise<Uint8Array>
{
  const response = await fetch(url, { cache: 'no-store' });
  if (!response.ok)
  {
    throw new Error(`Failed to fetch video: ${response.status}`);
  }

  return new Uint8Array(await response.arrayBuffer());
}

interface FfmpegAssets {
  FFmpeg: any;
  classWorkerURL: string;
  coreURL: string;
  wasmURL: string;
}

let assetsPromise: Promise<FfmpegAssets> | null = null;

// Downloads the script, worker and ~30 MB core once; every instance shares them
function loadAssets(): Promise<FfmpegAssets>
{
  if (!assetsPromise)
  {
    assetsPromise = (async () =>
    {
      await loadScript(FFMPEG_UMD_URL);

      const FFmpegWASM = (window as any).FFmpegWASM;
      if (!FFmpegWASM?.FFmpeg)
      {
        throw new Error('ffmpeg.wasm did not register on window');
      }

      // The FFmpeg class would otherwise spawn its worker straight from unpkg,
      // and browsers refuse cross-origin `new Worker(url)` with a SecurityError
      const [classWorkerURL, coreURL, wasmURL] = await Promise.all([
        toBlobURL(FFMPEG_WORKER_URL, 'text/javascript'),
        toBlobURL(`${CORE_BASE}/ffmpeg-core.js`, 'text/javascript'),
        toBlobURL(`${CORE_BASE}/ffmpeg-core.wasm`, 'application/wasm'),
      ]);

      return { FFmpeg: FFmpegWASM.FFmpeg, classWorkerURL, coreURL, wasmURL };
    })();

    // Allow a retry on the next attempt
    assetsPromise.catch(() =>
    {
      assetsPromise = null;
    });
  }

  return assetsPromise;
}

async function createInstance(): Promise<any>
{
  const { FFmpeg, classWorkerURL, coreURL, wasmURL } = await loadAssets();
  const ffmpeg = new FFmpeg();
  await ffmpeg.load({ classWorkerURL, coreURL, wasmURL });
  return ffmpeg;
}

// Each instance is a single-threaded worker, so several videos convert in
// parallel on separate cores. Capped because each one holds a whole video
// (input, output and decoder buffers) in its own memory.
interface InstancePool {
  acquire: (onProgress?: (p: TranscodeProgress) => void) => Promise<any>;
  release: (ffmpeg: any) => void;
  discard: (ffmpeg: any) => void;
}

function createPool(size: number): InstancePool
{
  const idle: any[] = [];
  let count = 0;
  const waiters: Array<{ resolve: (ffmpeg: any) => void; reject: (err: unknown) => void }> = [];

  const acquire = async (onProgress?: (p: TranscodeProgress) => void): Promise<any> =>
  {
    const free = idle.pop();
    if (free) return free;

    if (count < size)
    {
      count++;
      onProgress?.({ ratio: null, stage: 'loading-core' });
      try
      {
        return await createInstance();
      }
      catch (err)
      {
        count--;
        throw err;
      }
    }

    onProgress?.({ ratio: null, stage: 'queued' });
    const ffmpeg = await new Promise<any>((resolve, reject) => waiters.push({ resolve, reject }));
    onProgress?.({ ratio: 0, stage: 'transcoding' });
    return ffmpeg;
  };

  const release = (ffmpeg: any): void =>
  {
    const next = waiters.shift();
    if (next)
    {
      next.resolve(ffmpeg);
    }
    else
    {
      idle.push(ffmpeg);
    }
  };

  // A failed job may have left the worker unusable (out of memory, say):
  // drop it and give any waiting job a fresh one
  const discard = (ffmpeg: any): void =>
  {
    try
    {
      ffmpeg.terminate?.();
    }
    catch
    {
      /* ignore */
    }
    count--;

    const next = waiters.shift();
    if (!next) return;

    count++;
    createInstance().then(next.resolve, err =>
    {
      count--;
      next.reject(err);
    });
  };

  return { acquire, release, discard };
}

// Separate lanes so the video someone is waiting to watch never queues behind
// a batch of uploads: background (uploads) gets up to half the cores, max 3;
// interactive (playback) always has a converter of its own.
const pools: Record<TranscodeLane, InstancePool> = {
  background: createPool(Math.max(1, Math.min(3, Math.floor((navigator.hardwareConcurrency || 2) / 2)))),
  interactive: createPool(1),
};

// Reads the original file's bytes some other way than fetching its URL
export type VideoSourceLoader = () => Promise<Uint8Array>;

async function readSource(url: string, loadSource?: VideoSourceLoader): Promise<Uint8Array>
{
  if (loadSource)
  {
    try
    {
      return await loadSource();
    }
    catch (err)
    {
      console.warn('videoTranscode: source loader failed, fetching the URL directly', err);
    }
  }

  return fetchFile(url);
}

/**
 * Transcodes raw video bytes to an H.264/AAC MP4 blob.
 * The buffer is transferred to the ffmpeg worker, so `sourceBytes` is
 * unusable (detached) once this starts.
 */
export async function transcodeVideoBytes(
  sourceBytes: Uint8Array,
  options: TranscodeOptions,
  onProgress?: (p: TranscodeProgress) => void,
  signal?: AbortSignal,
): Promise<Blob>
{
  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');

  const pool = pools[options.lane];
  const ffmpeg = await pool.acquire(onProgress);
  let failed = false;

  const inputName = 'input_source';
  const outputName = 'output.mp4';

  const handleProgress = ({ progress }: { progress: number }) =>
  {
    onProgress?.({
      ratio: Math.max(0, Math.min(1, progress)),
      stage: 'transcoding',
    });
  };

  ffmpeg.on('progress', handleProgress);

  try
  {
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');

    await ffmpeg.writeFile(inputName, sourceBytes);

    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');

    // The WASM core runs single-threaded, so encode cost is everything.
    // yuv420p is what makes the result universally decodable.
    const exitCode = await ffmpeg.exec([
      '-i', inputName,
      '-vf', scaleFilter(options.maxLongSide),
      '-c:v', 'libx264',
      '-preset', options.preset,
      '-crf', String(options.crf),
      '-pix_fmt', 'yuv420p',
      '-profile:v', 'high',
      '-c:a', 'aac',
      '-b:a', '128k',
      '-movflags', '+faststart',
      outputName,
    ]);

    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    if (exitCode !== 0)
    {
      failed = true;
      throw new Error(`ffmpeg exited with code ${exitCode}`);
    }

    const data = await ffmpeg.readFile(outputName);

    // readFile hands back Uint8Array<ArrayBufferLike>, which TS 5.7+ will not
    // accept as a BlobPart (that requires an ArrayBuffer-backed view, not a
    // possibly-shared one). Copying into a fresh array settles the type and is
    // cheap next to the transcode itself.
    const bytes = new Uint8Array(data as ArrayLike<number>);
    return new Blob([bytes], { type: 'video/mp4' });
  }
  catch (err)
  {
    if ((err as DOMException)?.name !== 'AbortError') failed = true;
    throw err;
  }
  finally
  {
    ffmpeg.off?.('progress', handleProgress);

    if (failed)
    {
      pool.discard(ffmpeg);
    }
    else
    {
      // Free the emscripten FS entries; failures here are not interesting
      try { await ffmpeg.deleteFile(inputName); }
      catch { /* ignore */ }
      try { await ffmpeg.deleteFile(outputName); }
      catch { /* ignore */ }
      pool.release(ffmpeg);
    }
  }
}

/**
 * Transcodes `url` to H.264/AAC MP4 and returns a blob URL.
 * `loadSource`, when given, is tried before fetching `url` - storage that
 * does not send CORS headers can only be read through the API.
 * The caller owns the returned URL and must revokeObjectURL it.
 */
export async function transcodeToPlayableMp4(
  url: string,
  onProgress?: (p: TranscodeProgress) => void,
  signal?: AbortSignal,
  loadSource?: VideoSourceLoader,
): Promise<string>
{
  // Reopening a video in the same session skips the whole conversion
  const cached = transcodeCache.get(url);
  if (cached)
  {
    return URL.createObjectURL(cached);
  }

  const startedAt = performance.now();
  onProgress?.({ ratio: null, stage: 'loading-core' });

  // Both are network-bound, so fetch them side by side
  const [sourceBytes] = await Promise.all([
    readSource(url, loadSource),
    loadAssets(),
  ]);
  const loadedAt = performance.now();
  // Read now: the transcode transfers the buffer to the worker and detaches it
  const sourceSize = sourceBytes.byteLength;

  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');

  const blob = await transcodeVideoBytes(sourceBytes, PLAYBACK_TRANSCODE, onProgress, signal);

  rememberTranscode(url, blob);

  // Dev-only timing, to tell slow downloads apart from slow encodes
  if (import.meta.env.DEV)
  {
    const doneAt = performance.now();
    // eslint-disable-next-line no-console
    console.info(
      `videoTranscode: ${(sourceSize / 1048576).toFixed(1)} MB source - ` +
      `load ${Math.round(loadedAt - startedAt)} ms, transcode ${Math.round(doneAt - loadedAt)} ms`,
    );
  }

  return URL.createObjectURL(blob);
}
